"use client"

import { useCallback } from "react"
import { useRouter } from "next/navigation"

// 렌더링 시점 리프레시 실행 여부 관리 (무한 루프 방지)
const SESSION_REFRESH_KEY = "authguard_refresh_in_progress"

const markAsRefreshed = (): void => {
  if (typeof window !== "undefined") {
    sessionStorage.setItem(SESSION_REFRESH_KEY, "true")
  }
}

const isAlreadyRefreshed = (): boolean => {
  if (typeof window === "undefined") return false
  return sessionStorage.getItem(SESSION_REFRESH_KEY) === "true"
}

const clearRefreshFlag = (): void => {
  if (typeof window !== "undefined") {
    sessionStorage.removeItem(SESSION_REFRESH_KEY)
  }
}

export interface UseRenderingRefreshOptions {
  onRefresh?: () => void
  enableLogging?: boolean
}

// 기본 에러 감지 설정
const DEFAULT_ERROR_CONFIG = {
  classSelectors: [
    ".error-page",
    '[class*="error-"]:not([class*="-error-404"]):not([class*="error-500"]):not([class*="error-502"]):not([class*="error-503"])', // error- 클래스 중 HTTP 상태 코드 에러는 제외
    ".text-rl-gray-500", // 특정 회색 텍스트 스타일
  ],
  textPatterns: [
    "데이터가 없습니다",
    "오류가 발생했습니다",
    "네트워크 오류",
    "찾을 수 없는 페이지입니다",
    "네트워크 오류입니다",
  ],
}

export const useRenderingRefresh = (
  options: UseRenderingRefreshOptions = {},
) => {
  const router = useRouter()
  const { onRefresh, enableLogging = true } = options

  const performRefresh = useCallback(() => {
    markAsRefreshed()
    if (onRefresh) {
      onRefresh()
    } else {
      router.refresh()
    }
  }, [router, onRefresh])

  // 에러 상태 감지 함수
  const detectErrorState = useCallback((container: HTMLElement): boolean => {
    // 영구적 에러 요소들 먼저 체크 (새로고침 불필요)
    const permanentErrorSelectors = [
      ".next-error-h1", // Next.js 기본 에러 페이지
      '[class*="error-404"]', // 404 에러
      '[class*="error-500"]', // 500 에러
      '[class*="error-502"]', // 502 에러
      '[class*="error-503"]', // 503 에러
    ]

    if (container.querySelector(permanentErrorSelectors.join(", "))) {
      return false // 영구적 에러는 새로고침하지 않음
    }

    // CSS 선택자 기반 에러 감지
    const combinedSelector = DEFAULT_ERROR_CONFIG.classSelectors.join(", ")
    const hasErrorClass = !!container.querySelector(combinedSelector)

    if (hasErrorClass) {
      const textContent = container.textContent?.trim() || ""
      // 특정 회색 텍스트가 있는 경우에만 텍스트 패턴 체크
      if (container.querySelector(".text-rl-gray-500")) {
        return DEFAULT_ERROR_CONFIG.textPatterns.some((pattern) =>
          textContent.includes(pattern),
        )
      }
      return true
    }

    // 텍스트 패턴으로 에러 감지
    const textContent = container.textContent?.trim() || ""
    return DEFAULT_ERROR_CONFIG.textPatterns.some((pattern) =>
      textContent.includes(pattern),
    )
  }, [])

  const checkForRenderingFailure = useCallback(
    (container: HTMLElement) => {
      // 이미 리프레시 진행 중인지 확인
      if (isAlreadyRefreshed()) {
        if (enableLogging) {
          console.log("useRenderingRefresh: 이미 리프레시 진행 중. 스킵")
        }
        return
      }

      // 에러 페이지 감지
      if (detectErrorState(container)) {
        if (enableLogging) {
          console.log(
            "useRenderingRefresh: Server fetch 에러 페이지 감지됨. 리프레시 실행",
          )
        }
        performRefresh()
      }
    },
    [performRefresh, enableLogging, detectErrorState],
  )

  return {
    checkForRenderingFailure,
    clearRefreshFlag,
    isAlreadyRefreshed,
  }
}
